import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useKnowledgeBaseDocument, createDocument, updateDocument } from '../hooks/useKnowledgeBase';
import Button from '@/common/ui/components/Button';
import Input from '@/common/ui/components/Input';

export default function KnowledgeBaseEditor() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const { document: existingDoc, loading: loadingDoc } = useKnowledgeBaseDocument(id);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('Operations');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = ['Operations', 'Technical', 'Legal', 'Safety', 'Process'];

  // Load existing document data when editing
  useEffect(() => {
    if (existingDoc) {
      setTitle(existingDoc.title);
      setContent(existingDoc.content);
      setCategory(existingDoc.category);
    }
  }, [existingDoc]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!content.trim()) {
      setError('Content is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditMode && id) {
        const result = await updateDocument(id, title, content, category);
        if (result.success) {
          navigate(`/admin/knowledge-base/${id}`);
        } else {
          setError(result.error || 'Failed to update document');
        }
      } else {
        const result = await createDocument(title, content, category);
        if (result.success && result.documentId) {
          navigate(`/admin/knowledge-base/${result.documentId}`);
        } else {
          setError(result.error || 'Failed to create document');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Unsaved changes will be lost.')) {
      navigate(isEditMode ? `/admin/knowledge-base/${id}` : '/admin/knowledge-base');
    }
  };

  if (loadingDoc && isEditMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading document...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-title-lg font-inter text-text-primary">
                {isEditMode ? 'Edit Document' : 'New Document'}
              </h1>
              <p className="text-supporting text-text-secondary font-inter">
                {isEditMode ? 'Update your knowledge base document' : 'Create a new knowledge base document'}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleCancel}
                variant="ghost"
                size="md"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="primary"
                size="md"
                loading={saving}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Document'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          {/* Title Field */}
          <div>
            <Input
              label="Document Title *"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., CampusMeds Edge Case Protocol Handbook"
              required
            />
          </div>

          {/* Category Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-trust-blue focus:border-transparent"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Content Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content * (Markdown supported)
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Start typing your document content here. You can use markdown formatting..."
              rows={20}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-trust-blue focus:border-transparent font-mono text-sm resize-y"
              style={{ minHeight: '500px' }}
            />
            <p className="text-caption text-gray-500 mt-2">
              Tip: Use markdown syntax for formatting (headings: #, ##, ### | bold: **text** | lists: - item)
            </p>
          </div>

          {/* Action Buttons (Bottom) */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <Button
              onClick={handleCancel}
              variant="ghost"
              size="md"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="primary"
              size="md"
              loading={saving}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Document'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
